import {apiUrls} from '../../constants/urls';
import {httpService, HttpWrapperOptions} from '../../httpWrapper';
import {UpdateFirebaseIdRequestPayload} from '../../models/api';
import {getApiRoute} from '../../pipes';
import {storage, StorageKeys} from '../../storage';
import {getTokenResponse, IFcmService, messageHandlerFunction} from '../model';
import {messaging, vapidKey} from './messaging.web';

const minNextTokenCheckDelay = 2 * 60 * 1000;
export interface WebFirebaseNotification {
  data: any;
  from: string;
  priority: string;
}

export class FcmServiceWeb implements IFcmService<WebFirebaseNotification> {
  private messageHandlers: messageHandlerFunction<WebFirebaseNotification>[] = [];

  constructor() {
    navigator.serviceWorker?.addEventListener('message', this.handleMessageFromSW);
  }

  private handleMessageFromSW(event: MessageEvent) {
    if (typeof event.data === 'string') {
      window.location.pathname = event.data;
    }
  }

  async cleanToken(): Promise<void> {
    await storage.removeItem(StorageKeys.fcmToken);
    await storage.removeItem(StorageKeys.fcmTokenCheckTime);
  }

  public async isConnected(): Promise<boolean> {
    if (!messaging) {
      return false;
    }
    if (!window?.Notification) {
      return false;
    }
    if (window.Notification.permission === 'denied') {
      return false;
    }
    const token = await storage.getItem<string>(StorageKeys.fcmToken);

    return !!token;
  }

  async getToken(): Promise<getTokenResponse> {
    if (!messaging) {
      throw new Error('unsupported browser (no messaging)');
    }

    const now = new Date().getTime();
    const fcmTokenCheckTime = await storage.getItem<string>(StorageKeys.fcmTokenCheckTime);
    const prevToken = await storage.getItem<string>(StorageKeys.fcmToken);
    const userToken = await storage.getItem<string>(StorageKeys.token);
    const isTimeToCheckNewToken = !prevToken || !fcmTokenCheckTime || +fcmTokenCheckTime < now;

    let newToken = prevToken;

    try {
      if (!window?.Notification) {
        throw new Error('unsupported browser (no Notification)');
      }
      if (window.Notification.permission === 'denied') {
        throw new Error('Notification permission denied');
      }
      if (isTimeToCheckNewToken) {
        newToken = await messaging.getToken({
          vapidKey,
        });
        await storage.setItem(StorageKeys.fcmTokenCheckTime, now + minNextTokenCheckDelay);
      }
    } catch (err) {
      await storage.removeItem(StorageKeys.fcmTokenCheckTime);
      await storage.removeItem(StorageKeys.fcmToken);
      throw err;
    }

    if (newToken !== prevToken && newToken !== null && userToken) {
      await this.onTokenUpdate(newToken, prevToken);
      await storage.setItem(StorageKeys.fcmToken, newToken);
    }
    if (!newToken) {
      throw new Error('FCM token is empty');
    }

    return {
      token: newToken,
      previousToken: prevToken,
    };
  }

  getLatestBackgroundMessage(): Promise<WebFirebaseNotification | null> {
    return Promise.resolve(null);
  }

  messageHandler(remoteMessage: WebFirebaseNotification): Promise<void> {
    if (this.messageHandlers.length) {
      this.messageHandlers.forEach((item) => item(remoteMessage, false));
    }
    return Promise.resolve(undefined);
  }

  registerMessageHandler(handler: messageHandlerFunction<WebFirebaseNotification>): void {
    if (handler) {
      this.messageHandlers.push(handler);
    }
  }

  unregisterMessageHandler(handler?: messageHandlerFunction<WebFirebaseNotification>): void {
    if (handler) {
      this.messageHandlers = this.messageHandlers.filter((item) => item !== handler);
    } else {
      this.messageHandlers = [];
    }
  }

  private async onTokenUpdate(newToken: string, prevToken: string | null): Promise<void> {
    const authToken = await storage.getItem<string>(StorageKeys.token);

    if (!authToken) {
      return;
    }

    await httpService.post<void>(
      getApiRoute(apiUrls.updateFirebaseId),
      {
        firebaseId: newToken,
        previousFirebaseId: prevToken,
      } as UpdateFirebaseIdRequestPayload,
      new HttpWrapperOptions().Errors(false).ThrowException(true),
    );
  }
}
